import React, { useState } from "react";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Input from "../../../../components/form/Input";
import Select from "../../../../components/form/Select";
import { useFormikContext } from "formik";
import DimensionsModal from "../../../../components/TablePro/fields/DimensionsField";
import { formatLocationDimensions } from "../../../../utils/dimensions";
import {
  CreatingModeField,
  LocationNameFields,
  LocationPreview,
} from "./LocationNamingFields";
import MissingCentersCreation from "./MissingCentersCreation";
import { EXISTING_CENTER_SLOT } from "../../../../utils/locationNameRange";

const baseLocationTypes = [
  ["floor", "Floor"],
  ["inbound", "Inbound"],
  ["offsite", "Offsite"],
  ["pallet", "Pallet"],
  ["rack", "Rack"],
  ["sofa_rack", "Sofa Rack"],
  ["trailer", "Trailer"],
  ["vault", "Vault"],
];

// Labels only -- the "LR"/"LCR" values are what slotSuffixes keys off and what
// the mutation sends as `slot`, so they must not change. existing_center is
// handled by warehouseLocationAddMissingCenters instead.
const slotOptions = [
  ["LR", "Left, Right"],
  ["LCR", "Left, Center, Right"],
  [EXISTING_CENTER_SLOT, "Center On Existing Rack"],
  ["Single", "Single"],
  ["Custom", "Custom"],
];

export default function BaseLocationCreation({ warehouseId }) {
  const { values, setFieldValue } = useFormikContext();
  const [openDimensions, setOpenDimensions] = useState(false);
  const currentDimensions = values?.dimensions || [];
  const hasCreatingMode = values.creatingMode === "single" || values.creatingMode === "multiple";
  const isMultiple = values.creatingMode === "multiple";
  const isRack = values.locationType === "rack";
  const isExistingCenter = values.slot === EXISTING_CENTER_SLOT;
  const hasType = Boolean(values.locationType);
  const hasSlot =
    !isRack || Boolean(values.slot && (values.slot !== "Custom" || (values.customSlot || "").trim()));
  const hasName = isMultiple
    ? Boolean((values.locationFrom || "").trim() && (values.locationTo || "").trim())
    : Boolean((values.locationName || "").trim());
  const squareFeetLabel = isRack ? "Slot Square Feet" : "Location Square Feet";
  const dimensionsLabel = isRack ? 'Slot Dimensions W" x D" x H"' : 'Dimensions W" x D" x H"';

  const handleDimensionsSave = dims => {
    setFieldValue("dimensions", dims);
  };

  return (
    <Box>
      <Box display="flex" flexDirection="column" gap={2}>
        <CreatingModeField />
        {hasCreatingMode && (
          <Select
            size="medium"
            name="locationType"
            label="Location Type"
            options={baseLocationTypes}
            showNAButton={false}
          />
        )}
        {hasType && isRack && (
          <Select
            size="medium"
            name="slot"
            label="Slot"
            options={slotOptions}
            showNAButton={false}
          />
        )}
        {hasType && isRack && values.slot === "Custom" && (
          <Input size="medium" name="customSlot" label="Custom Slot Value" showNAButton={false} />
        )}
        {hasType && isRack && isExistingCenter && (
          <MissingCentersCreation warehouseId={warehouseId} />
        )}
        {hasType && hasSlot && !isExistingCenter && <LocationNameFields />}
        {hasName && !isExistingCenter && (
          <Input
            size="medium"
            name="sqFtOfSlot"
            label={squareFeetLabel}
            type="number"
            showNAButton={false}
          />
        )}
        {hasName && !isExistingCenter && (
          <TextField
            fullWidth
            size="medium"
            variant="outlined"
            label={dimensionsLabel}
            value={formatLocationDimensions(currentDimensions)}
            onClick={() => setOpenDimensions(true)}
            InputProps={{
              readOnly: true,
            }}
          />
        )}
      </Box>
      <LocationPreview />
      {openDimensions && (
        <DimensionsModal
          field={{ label: "Dimensions" }}
          value={currentDimensions}
          onSubmit={handleDimensionsSave}
          nextRow={() => {}}
          onClose={() => setOpenDimensions(false)}
        />
      )}
    </Box>
  );
}
